import { useState, useEffect } from "react";
import { ouvrirFichier, getPendingValidations, validateFoodTruck, rejectFoodTruck, getFoodTrucks, sendRelance, resetRelances } from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  UserCheck,
  UserX,
  Clock,
  FileText,
  Building2,
  Phone,
  Mail,
  ChefHat,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Bell,
  RefreshCw,
  Search,
  Eye,
  CreditCard,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";

export default function ValidationsPage() {
  const [pendingValidations, setPendingValidations] = useState([]);
  const [allFoodTrucks, setAllFoodTrucks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFT, setSelectedFT] = useState(null);
  const [actionDialog, setActionDialog] = useState({ open: false, type: null, ft: null });
  const [rejectReason, setRejectReason] = useState("");
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("pending");

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [pendingRes, allFTRes] = await Promise.all([
        getPendingValidations(),
        getFoodTrucks()
      ]);
      setPendingValidations(pendingRes.data);
      setAllFoodTrucks(allFTRes.data);
    } catch (error) {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  const handleValidate = async () => {
    if (!actionDialog.ft) return;
    try {
      await validateFoodTruck(actionDialog.ft.id);
      toast.success("Food truck validé avec succès");
      setActionDialog({ open: false, type: null, ft: null });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la validation");
    }
  };

  const handleReject = async () => {
    if (!actionDialog.ft) return;
    try {
      await rejectFoodTruck(actionDialog.ft.id, rejectReason);
      toast.success("Inscription rejetée");
      setActionDialog({ open: false, type: null, ft: null });
      setRejectReason("");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors du rejet");
    }
  };

  const handleSendRelance = async (ftId) => {
    try {
      const response = await sendRelance(ftId);
      toast.success(response.data.message);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi de la relance");
    }
  };

  const handleResetRelances = async (ftId) => {
    try {
      await resetRelances(ftId);
      toast.success("Compteur de relances remis à zéro");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  const getStatusBadge = (statut) => {
    switch (statut) {
      case "en_attente_validation":
        return <Badge className="bg-amber-500">En attente</Badge>;
      case "actif":
        return <Badge className="bg-green-500">Actif</Badge>;
      case "bloque_documents":
        return <Badge className="bg-red-500">Docs expirés</Badge>;
      case "bloque_paiement":
        return <Badge className="bg-orange-500">Bloqué paiement</Badge>;
      case "inactif":
        return <Badge className="bg-gray-500">Inactif</Badge>;
      default:
        return <Badge>{statut}</Badge>;
    }
  };

  const filteredFoodTrucks = allFoodTrucks.filter(ft => {
    const matchesSearch = !search || 
      ft.nom_food_truck.toLowerCase().includes(search.toLowerCase()) ||
      ft.email.toLowerCase().includes(search.toLowerCase());
    
    if (activeTab === "pending") {
      return ft.statut === "en_attente_validation" && matchesSearch;
    } else if (activeTab === "blocked") {
      return (ft.statut === "bloque_documents" || ft.statut === "bloque_paiement") && matchesSearch;
    } else if (activeTab === "payment") {
      return ft.solde_du > 0 && ft.statut !== "en_attente_validation" && matchesSearch;
    }
    return matchesSearch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="validations-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl md:text-4xl font-black text-primary">
          Gestion des inscriptions
        </h1>
        <p className="text-muted-foreground mt-1">
          Validations, blocages et relances
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="bg-white border-[#E5E0DC]">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-100">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">En attente</p>
                <p className="text-xl font-bold text-secondary">
                  {allFoodTrucks.filter(ft => ft.statut === "en_attente_validation").length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-[#E5E0DC]">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-100">
                <FileText className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Docs expirés</p>
                <p className="text-xl font-bold text-secondary">
                  {allFoodTrucks.filter(ft => ft.statut === "bloque_documents").length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-[#E5E0DC]">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-orange-100">
                <CreditCard className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Bloqués paiement</p>
                <p className="text-xl font-bold text-secondary">
                  {allFoodTrucks.filter(ft => ft.statut === "bloque_paiement").length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-[#E5E0DC]">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <AlertTriangle className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Impayés</p>
                <p className="text-xl font-bold text-secondary">
                  {allFoodTrucks.filter(ft => ft.solde_du > 0 && ft.statut !== "en_attente_validation").length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-[#F0EBE6]">
          <TabsTrigger value="pending" className="data-[state=active]:bg-primary data-[state=active]:text-white">
            En attente ({allFoodTrucks.filter(ft => ft.statut === "en_attente_validation").length})
          </TabsTrigger>
          <TabsTrigger value="blocked" className="data-[state=active]:bg-primary data-[state=active]:text-white">
            Bloqués ({allFoodTrucks.filter(ft => ft.statut === "bloque_documents" || ft.statut === "bloque_paiement").length})
          </TabsTrigger>
          <TabsTrigger value="payment" className="data-[state=active]:bg-primary data-[state=active]:text-white">
            Impayés ({allFoodTrucks.filter(ft => ft.solde_du > 0 && ft.statut !== "en_attente_validation").length})
          </TabsTrigger>
        </TabsList>

        {/* Search */}
        <div className="relative max-w-md mt-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            placeholder="Rechercher..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 border-[#E5E0DC] bg-white"
          />
        </div>

        <TabsContent value="pending" className="mt-4">
          {filteredFoodTrucks.length > 0 ? (
            <div className="space-y-4">
              {filteredFoodTrucks.map((ft) => (
                <Card key={ft.id} className="bg-white border-[#E5E0DC]">
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-start gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <ChefHat className="w-6 h-6 text-primary" />
                          <h3 className="font-heading text-xl font-bold text-secondary">
                            {ft.nom_food_truck}
                          </h3>
                          {getStatusBadge(ft.statut)}{ft.type_validation === 'document' && <Badge className="bg-violet-600 ml-2">Document : {ft.document_en_attente}</Badge>}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-muted-foreground">
                          <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4" />
                            {ft.email}
                          </div>
                          <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4" />
                            {ft.telephone}
                          </div>
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4" />
                            SIREN: {ft.societe?.siren || "N/A"}
                          </div>
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4" />
                            Inscrit le {format(parseISO(ft.created_at), "dd/MM/yyyy", { locale: fr })}
                          </div>
                        </div>
                        
                        {/* Documents */}
                        {ft.documents && ft.documents.length > 0 && (
                          <div className="mt-4">
                            <p className="text-sm font-semibold text-secondary mb-2">Documents fournis :</p>
                            <div className="flex flex-wrap gap-2">
                              {ft.documents.map((doc) => (
                                <a
                                  key={doc.id}
                                  href={`${process.env.REACT_APP_BACKEND_URL}${doc.url}`}
                                  onClick={(e) => { e.preventDefault(); ouvrirFichier(doc.url).catch(() => toast.error("Document indisponible ou accès refusé")); }}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 px-2 py-1 bg-[#F0EBE6] rounded text-xs hover:bg-primary/10"
                                >
                                  <FileText className="w-3 h-3" />
                                  {doc.nom}
                                  {doc.date_expiration && (
                                    <span className="text-muted-foreground">
                                      (exp: {doc.date_expiration})
                                    </span>
                                  )}
                                </a>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-green-500 text-green-600 hover:bg-green-50"
                          onClick={() => setActionDialog({ open: true, type: "validate", ft })}
                          data-testid={`validate-${ft.id}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Valider
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-red-500 text-red-600 hover:bg-red-50"
                          onClick={() => setActionDialog({ open: true, type: "reject", ft })}
                          data-testid={`reject-${ft.id}`}
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Rejeter
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="bg-white border-[#E5E0DC]">
              <CardContent className="p-12 text-center">
                <CheckCircle className="w-16 h-16 mx-auto text-green-500 mb-4" />
                <h3 className="font-heading text-xl text-secondary mb-2">Aucune validation en attente</h3>
                <p className="text-muted-foreground">
                  Toutes les inscriptions ont été traitées
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="blocked" className="mt-4">
          {filteredFoodTrucks.length > 0 ? (
            <Card className="bg-white border-[#E5E0DC]">
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#F9F7F5]">
                      <TableHead>Food Truck</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead>Raison</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredFoodTrucks.map((ft) => (
                      <TableRow key={ft.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{ft.nom_food_truck}</p>
                            <p className="text-sm text-muted-foreground">{ft.email}</p>
                          </div>
                        </TableCell>
                        <TableCell>{getStatusBadge(ft.statut)}{ft.type_validation === 'document' && <Badge className="bg-violet-600 ml-2">Document : {ft.document_en_attente}</Badge>}</TableCell>
                        <TableCell>
                          {ft.statut === "bloque_documents" && "Documents expirés"}
                          {ft.statut === "bloque_paiement" && `3+ relances impayées`}
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleResetRelances(ft.id)}
                            className="border-[#E5E0DC]"
                          >
                            <RefreshCw className="w-4 h-4 mr-1" />
                            Débloquer
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-white border-[#E5E0DC]">
              <CardContent className="p-12 text-center">
                <CheckCircle className="w-16 h-16 mx-auto text-green-500 mb-4" />
                <h3 className="font-heading text-xl text-secondary mb-2">Aucun compte bloqué</h3>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="payment" className="mt-4">
          {filteredFoodTrucks.length > 0 ? (
            <Card className="bg-white border-[#E5E0DC]">
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#F9F7F5]">
                      <TableHead>Food Truck</TableHead>
                      <TableHead>Solde dû</TableHead>
                      <TableHead>Relances</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredFoodTrucks.map((ft) => (
                      <TableRow key={ft.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{ft.nom_food_truck}</p>
                            <p className="text-sm text-muted-foreground">{ft.email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="font-semibold text-red-600">{ft.solde_du?.toFixed(2)} €</span>
                        </TableCell>
                        <TableCell>
                          <Badge className={ft.relances_impaye >= 3 ? "bg-red-500" : "bg-amber-500"}>
                            {ft.relances_impaye || 0} / 3
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleSendRelance(ft.id)}
                              disabled={ft.relances_impaye >= 3}
                              className="border-amber-500 text-amber-600 hover:bg-amber-50"
                              data-testid={`relance-${ft.id}`}
                            >
                              <Bell className="w-4 h-4 mr-1" />
                              Relancer
                            </Button>
                            {ft.relances_impaye > 0 && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleResetRelances(ft.id)}
                                className="border-[#E5E0DC]"
                              >
                                <RefreshCw className="w-4 h-4 mr-1" />
                                Reset
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-white border-[#E5E0DC]">
              <CardContent className="p-12 text-center">
                <CheckCircle className="w-16 h-16 mx-auto text-green-500 mb-4" />
                <h3 className="font-heading text-xl text-secondary mb-2">Aucun impayé</h3>
                <p className="text-muted-foreground">
                  Tous les membres sont à jour
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Validation Dialog */}
      <Dialog 
        open={actionDialog.open && actionDialog.type === "validate"} 
        onOpenChange={() => setActionDialog({ open: false, type: null, ft: null })}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-primary flex items-center gap-2">
              <CheckCircle className="w-6 h-6 text-green-500" />
              Valider {actionDialog.ft?.type_validation === 'document' ? 'le document' : "l'inscription"}
            </DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir valider {actionDialog.ft?.type_validation === 'document' ? 'le document' : "l'inscription"} de <strong>{actionDialog.ft?.nom_food_truck}</strong> ?
              <br /><br />
              {actionDialog.ft?.type_validation === 'document' ? "Le compte sera reactive. Aucune cotisation ne sera creee." : "Une cotisation annuelle de 150 € sera automatiquement créée."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialog({ open: false, type: null, ft: null })}>
              Annuler
            </Button>
            <Button className="bg-green-600 hover:bg-green-700" onClick={handleValidate}>
              <CheckCircle className="w-4 h-4 mr-2" />
              Valider
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rejection Dialog */}
      <Dialog 
        open={actionDialog.open && actionDialog.type === "reject"} 
        onOpenChange={() => setActionDialog({ open: false, type: null, ft: null })}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-red-600 flex items-center gap-2">
              <XCircle className="w-6 h-6" />
              Rejeter {actionDialog.ft?.type_validation === 'document' ? 'le document' : "l'inscription"}
            </DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir rejeter {actionDialog.ft?.type_validation === 'document' ? 'le document' : "l'inscription"} de <strong>{actionDialog.ft?.nom_food_truck}</strong> ?
              <br /><br />
              Cette action supprimera définitivement le compte et les données associées.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              placeholder="Raison du rejet (optionnel)"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="border-[#E5E0DC]"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialog({ open: false, type: null, ft: null })}>
              Annuler
            </Button>
            <Button variant="destructive" onClick={handleReject}>
              <XCircle className="w-4 h-4 mr-2" />
              Rejeter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
